

import { CommonModal } from 'components/common';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import './dropdownSelectModal.css';
import { IoIosAddCircle, IoMdRadioButtonOn } from 'react-icons/io';
import StorageService from 'utils/StorageServices';
import { getVehicleGroupListData } from '_services/accountsServices/vehicleGroupServices';
import { getUser } from '_services';
import FirmImg from 'assets/img/firm.png';

const LIMIT = 20;

const DropdownSelectModal = (props) => {
    const {
        isOpen,
        closeModal = () => { },
        onSubmit = () => { },
        fetchListFn = null,
    } = props;

    const companyIdRef = useRef(StorageService.getCompanyId());
    const firmIdRef = useRef(StorageService.getFirmId());

    const [listArray, setListArray] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [currentData, setCurrentData] = useState({});
    const [search, setSearch] = useState('');
    const [offset, setOffset] = useState(1);

    const scrollRef = useRef(null);
    const searchTimerRef = useRef(null);

    // ─── Fetchers ─────────────────────────────────────────────────────────────

    const fetchUsers = async (searchVal, pageOffset, append) => {
        try {
            const response = await getUser({
                company_id: companyIdRef.current,
                offset: pageOffset,
                limit: LIMIT,
                search: searchVal,
                firm_id: firmIdRef.current || '',
            });
            if (response?.responseCode === 200) {
                const users = response.data.user_list.map((user) => ({
                    label: user.company_user_name,
                    value: user?.user_id?._id,
                    subTexts: [user?.email || '', user?.role || ''].filter(Boolean),
                }));
                setHasMore(users.length === LIMIT);
                setListArray((prev) => append ? [...prev, ...users] : users);
            }
        } catch (_) { }
    };

    const fetchVehicleGroups = async (searchVal, pageOffset, append) => {
        try {
            const res = await getVehicleGroupListData({
                company_id: companyIdRef.current,
                search: searchVal,
                offset: pageOffset,
                limit: LIMIT,
            });
            if (res?.responseCode === 200) {
                const groups = res.data.vehicle_group_list.map((el) => ({
                    label: el?.name,
                    value: el?._id,
                    subTexts: [
                        el?.description || '',
                        el?.vehicle_count ? `${el.vehicle_count} vehicles` : '',
                    ].filter(Boolean),
                }));
                setHasMore(groups.length === LIMIT);
                setListArray((prev) => append ? [...prev, ...groups] : groups);
            }
        } catch (_) { }
    };

    const fetchCustom = async (searchVal, pageOffset, append) => {
        try {
            const result = await fetchListFn(searchVal, pageOffset, LIMIT);
            setHasMore(result?.hasMore ?? false);
            setListArray((prev) => append ? [...prev, ...(result?.list || [])] : result?.list || []);
        } catch (_) { }
    };

    // ─── Core load ────────────────────────────────────────────────────────────

    const loadData = useCallback(async (searchVal, pageOffset, append = false) => {
        append ? setLoadingMore(true) : setLoading(true);

        if (fetchListFn) {
            await fetchCustom(searchVal, pageOffset, append);
        } else if (isOpen?.type === 'updated_user_id') {
            await fetchUsers(searchVal, pageOffset, append);
        } else if (isOpen?.type === 'vehicle_group_id') {
            await fetchVehicleGroups(searchVal, pageOffset, append);
        }

        append ? setLoadingMore(false) : setLoading(false);
    }, [isOpen?.type, fetchListFn]);

    // ─── Search debounce ──────────────────────────────────────────────────────

    useEffect(() => {
        if (!isOpen?.action) return;
        clearTimeout(searchTimerRef.current);
        searchTimerRef.current = setTimeout(() => {
            setOffset(1);
            setHasMore(true);
            loadData(search, 1, false);
        }, 350);
        return () => clearTimeout(searchTimerRef.current);
    }, [search, isOpen?.type, isOpen?.action]);

    // ─── Reset on open ────────────────────────────────────────────────────────

    useEffect(() => {
        setCurrentData(isOpen?.data?.value ? isOpen.data : {});
        setSearch('');
        setListArray([]);
        setOffset(1);
        setLoading(true); 
    }, [isOpen?.type, isOpen?.action]);

    // ─── Infinite scroll ──────────────────────────────────────────────────────

    const handleScroll = useCallback(() => {
        const el = scrollRef.current;
        if (!el || loadingMore || !hasMore) return;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
            const next = offset + 1;
            setOffset(next);
            loadData(search, next, true);
        }
    }, [offset, loadingMore, hasMore, search, loadData]);

    useEffect(() => {
        const el = scrollRef.current;
        if (el) el.addEventListener('scroll', handleScroll);
        return () => { if (el) el.removeEventListener('scroll', handleScroll); };
    }, [handleScroll]);

    // ─── Handlers ─────────────────────────────────────────────────────────────

    const handleOnChange = (data) => {
        setCurrentData(data);
        onSubmit({ [isOpen?.type]: data });
        closeModal(false);
    };

    const handleAddNew = () => {
        if (typeof isOpen?.onAddNew === 'function') {
            isOpen.onAddNew();
            closeModal(false);
        }
    };

    const handleEditItem = (e, item) => {
        e.stopPropagation();
        if (typeof isOpen?.onEdit === 'function') {
            isOpen.onEdit(item);
            closeModal(false);
        }
    };

    // ─── Helpers ──────────────────────────────────────────────────────────────

    const getBadgeStyle = (color) => {
        const map = {
            green: { background: '#e6f9ec', color: '#16a34a', border: '#bbf7d0' },
            orange: { background: '#fff7ed', color: '#ea580c', border: '#fed7aa' },
            blue: { background: '#eff6ff', color: '#2563eb', border: '#bfdbfe' },
            purple: { background: '#f5f3ff', color: '#7c3aed', border: '#ddd6fe' },
            red: { background: '#fef2f2', color: '#dc2626', border: '#fecaca' },
        };
        return map[color] || map.blue;
    };

    const getStatusDotColor = (color) => {
        const map = {
            green: '#16a34a',
            orange: '#ea580c',
            blue: '#2563eb',
            purple: '#7c3aed',
            red: '#dc2626',
        };
        return map[color] || '#16a34a';
    };

    // ─── Render ───────────────────────────────────────────────────────────────

    return (
        <CommonModal {...props} isFooter={false}>
            <div className="__modalSel_content">

                {/* Search */}
                <div className="__modalSel_search">
                    <input
                        type="text"
                        placeholder="Search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        autoFocus
                    />
                </div>

                {/* Add New */}
                {isOpen?.showAddNew && (
                    <div className="__modalSel_addNew" onClick={handleAddNew}>
                        {/* <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 18 18" fill="none">
                            <path d="M8.61539 0C6.33902 0.0276205 4.16368 0.944178 2.55393 2.55393C0.944178 4.16368 0.0276205 6.33902 0 8.61539C0.0276205 10.8917 0.944178 13.0671 2.55393 14.6768C4.16368 16.2866 6.33902 17.2031 8.61539 17.2308C10.8917 17.2031 13.0671 16.2866 14.6768 14.6768C16.2866 13.0671 17.2031 10.8917 17.2308 8.61539C17.2031 6.33902 16.2866 4.16368 14.6768 2.55393C13.0671 0.944178 10.8917 0.0276205 8.61539 0ZM13.5385 9.23077H9.23077V13.5385H8V9.23077H3.69231V8H13.5385V9.23077Z" />
                        </svg> */}
                        <div style={{ display: 'flex', alignItems: 'center', fontSize: 19, marginTop: -2 }}>
                            <IoIosAddCircle />
                        </div>
                        <span>Add New {isOpen?.name || ''}</span>
                    </div>
                )}

                {/* List */}
                <ul className="__modalSelListUl" ref={scrollRef}>

                    {loading ? (
                        <li className="__modalSelListLi" style={{ justifyContent: 'center' }}>
                            <div className="__modalSel_spinner" />
                        </li>
                    ) : listArray.length > 0 ? (
                        <>
                            {listArray.map((item, index) => {
                                const isActive = currentData?.value === item.value;
                                const badgeStyle = item.badge ? getBadgeStyle(item.badge.color) : null;
                                const statusColor = item.status ? getStatusDotColor(item.status.color) : null;
                                // show vertical divider only when right col has something
                                const hasRight = item.status || isOpen?.onEdit;

                                return (
                                    <li
                                        key={item.value + index}
                                        className={`__modalSelListLi${isActive ? ' active' : ''} __modalSelListLi_hoverable`}
                                        onClick={() => handleOnChange(item)}
                                    >
                                        {/* ── LEFT: radio + label/badge + subtexts ── */}
                                        <div className="__modalSelListFlx">
                                            <IoMdRadioButtonOn />
                                            <div className="__modalSel_itemWrap">

                                                {/* label + ownership badge */}
                                                <div className="__modalSel_labelRow">
                                                    <span className="__modalSel_labelTxt">{item.label}</span>
                                                    {item.badge && <span className="__modalSel_subDivider" />}
                                                    {item.badge && (
                                                        <span
                                                            className="__modalSel_badge"
                                                            style={{
                                                                background: badgeStyle.background,
                                                                color: badgeStyle.color,
                                                                border: `1px solid ${badgeStyle.border}`,
                                                            }}
                                                        >
                                                            {item.badge.text}
                                                        </span>
                                                    )}
                                                </div>

                                                {/*
                                                    Subtexts row:
                                                    [🏢 icon] [firm name] │ [vehicle type]
                                                    The __modalSel_subDivider is the vertical pipe
                                                    between each subText segment.
                                                */}
                                                {item.subTexts?.length > 0 && (
                                                    <div className="__modalSel_subTexts">
                                                        <img
                                                            src={FirmImg}
                                                            className="firm_icon_img"
                                                            style={{ filter: "invert(60%) sepia(90%) saturate(900%) hue-rotate(5deg)" }}
                                                        />
                                                        {item.subTexts.map((txt, i) => (
                                                            <React.Fragment key={i}>
                                                                <span className="__modalSel_subText">{txt}</span>
                                                                {i < item.subTexts.length - 1 && (
                                                                    <span className="__modalSel_subDivider" />
                                                                )}
                                                            </React.Fragment>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* ── VERTICAL DIVIDER between left and right ── */}
                                        {/* {hasRight && <span className="__modalSel_vDivider" />} */}

                                        {/* ── RIGHT: status dot + text, edit icon ── */}
                                        {hasRight && (
                                            <div className="__modalSel_rightCol">
                                                {item.status && (
                                                    <span className="__modalSel_status">
                                                        <span
                                                            className="__modalSel_statusDot"
                                                            style={{ background: statusColor }}
                                                        />
                                                        <span style={{ color: statusColor }}>
                                                            {item.status.text}
                                                        </span>
                                                    </span>
                                                )}

                                                {isOpen?.onEdit && (
                                                    <span
                                                        className="__modalSel_itemEdit"
                                                        onClick={(e) => handleEditItem(e, item)}
                                                        title={`Edit ${item.label}`}
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                                        </svg>
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </li>
                                );
                            })}

                            {loadingMore && (
                                <li className="__modalSelListLi" style={{ justifyContent: 'center' }}>
                                    <div className="__modalSel_spinner" />
                                </li>
                            )}

                            {!hasMore && !loadingMore && (
                                <li className="__modalSel_endMsg">No more results</li>
                            )}
                        </>
                    ) : (
                        <li className="__modalSelListLi" style={{ justifyContent: 'center' }}>
                            <span>No data found</span>
                        </li>
                    )}

                </ul>
            </div>
        </CommonModal>
    );
};

export default DropdownSelectModal;